import {
	DocumentData,
	DocumentSnapshot,
	QueryConstraint,
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDocs,
	limit,
	orderBy,
	query,
	serverTimestamp,
	startAfter,
} from 'firebase/firestore';

import {db} from '../firebase';
import {getDebug} from './GeneralUse';
import {usePostsStore} from '../stores/PostsStore';

//
// ****************************************************************************************************
//
//
// METHODS
// createPost
// deletePostDocument
// readPostDocuments
// readPostDocumentsLimit
// refreshPostDocuments
//
const DEBUG = getDebug('posts');
// ****************************************************************************************************
//

/*
//
*/
export const createPostDocument = async (
	content: string,
	uid: string,
	username: string,
) => {
	DEBUG && console.trace(`createPostDocument('${content}', '${uid}')`);

	try {
		const postsCollection = collection(db, 'posts');

		const postDoc = {
			content: content,
			created_at: serverTimestamp(),
			uid: uid,
			username: username,
		};

		await addDoc(postsCollection, postDoc);
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const deletePostDocument = async (documentId: string) => {
	DEBUG && console.trace(`deletePostDocument(${documentId})`);

	try {
		const docRef = doc(db, 'posts', documentId);

		await deleteDoc(docRef);
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
// We HAVE to specify a valid field.
// We HAVE to specify a valid sort direction.
// We HAVE to specify whether or not we use the query cursor (i.e. do we want to return only the docs STARTING AFTER the lastVisiblePost document location in the query result?)
// We OPTIONALLY specify a limit to the number of docs returned.
//
// How to use (1 = returned document from with the total [collection], 0 = not returned)
// First call: readPostDocuments('created_at', 'desc', true, 5) ===> returns [1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
// Second call: readPostDocuments('created_at', 'desc', true, 5) ===> returns [0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0]
// Third call: readPostDocuments('created_at', 'desc', true, 5) ===> returns [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1]
//
// This essentially allows us to do something like: scroll through a list of posts ordered by creation date, loading another four at a time when we scroll to the
// bottom of the existing container.
*/
const readPostDocuments = async (
	fieldName: 'created_at',
	sortDirection: 'asc' | 'desc',
	useCursor: boolean,
	limitCount?: number,
) => {
	DEBUG &&
		console.trace(
			`readPostDocuments('${fieldName}', '${sortDirection}', ${useCursor}, ${limitCount})`,
		);

	try {
		// Zustand
		const lastVisiblePost = usePostsStore.getState().lastVisiblePost;

		const postsCollection = collection(db, 'posts');

		const queryConstraints: QueryConstraint[] = [
			orderBy(fieldName, sortDirection),
		];
		if (useCursor && lastVisiblePost) {
			queryConstraints.push(startAfter(lastVisiblePost));
		} else {
			DEBUG && console.trace(`useCursor disabled or no lastVisiblePost`);
		}
		if (limitCount) {
			queryConstraints.push(limit(limitCount));
		} else {
			DEBUG && console.trace(`no limitCount provided`);
		}

		const q = query(postsCollection, ...queryConstraints);

		const docSnaps = await getDocs(q);
		const docs = docSnaps.docs;

		return docs;
	} catch (error) {
		DEBUG && console.error(error);
		return null;
	}
};

/*
// This is the only way the frontend is allowed to use the readPostDocuments() method. We neuter the front end, only allowing a CUSTOM BUT NON-NEGOTIABLE limit to the
// number of documents returned. Otherwise we are always returning from the latest post to the earliest, and using the query cursor in the process.
*/
export const readPostDocumentsLimit = async (limitCount: number) => {
	DEBUG && console.groupCollapsed('Populate Posts from Firestore');
	DEBUG && console.trace(`readPostDocumentsLimit(${limitCount})`);

	try {
		// Zustand
		const posts = usePostsStore.getState().posts;
		const setPosts = usePostsStore.getState().setPosts;
		const setLastVisiblePost = usePostsStore.getState().setLastVisiblePost;
		const loadedAllPosts = usePostsStore.getState().loadedAllPosts;
		const setLoadedAllPosts = usePostsStore.getState().setLoadedAllPosts;

		if (limitCount >= 1 && !loadedAllPosts) {
			const docs = await readPostDocuments(
				'created_at',
				'desc',
				true,
				limitCount,
			);

			if (docs && docs.length) {
				let newPosts: DocumentSnapshot<DocumentData, DocumentData>[] = [];
				if (posts) {
					newPosts = [...posts, ...docs];
				} else {
					newPosts = docs;
				}

				DEBUG && console.trace(`setPosts(`, newPosts, `)`);
				setPosts(newPosts);

				const lastVisiblePostLive = docs[docs.length - 1];
				DEBUG &&
					console.trace(
						`setLastVisiblePost(`,
						lastVisiblePostLive,
						`)
aka`,
						lastVisiblePostLive.data(),
					);
				setLastVisiblePost(lastVisiblePostLive);

				return true;
			} else {
				DEBUG &&
					console.log(
						`docs is either null (error in readPostDocuments() or empty`,
					);
				DEBUG && console.trace(`setLastVisiblePost(null)`);
				DEBUG && console.trace(`setLoadedAllPosts(true)`);
				setLastVisiblePost(null);
				setLoadedAllPosts(true);
			}
		} else {
			DEBUG && console.log(`limitCount is < 1 or we have loaded all`);
		}
	} catch (error) {
		DEBUG && console.error(error);
	}

	DEBUG && console.groupEnd();

	return false;
};

/*
//
*/
export const refreshPostDocuments = async (limitCount: number) => {
	DEBUG && console.trace(`refreshPostDocuments(${limitCount})`);

	try {
		const setPosts = usePostsStore.getState().setPosts;
		const setLastVisiblePost = usePostsStore.getState().setLastVisiblePost;
		const setLoadedAllPosts = usePostsStore.getState().setLoadedAllPosts;

		setPosts(null);
		setLastVisiblePost(null);
		setLoadedAllPosts(false);

		await readPostDocumentsLimit(limitCount);
	} catch (error) {
		DEBUG && console.error(error);
	}
};
